import { useId } from "@reach/auto-id";
import { type FC, type SVGProps, forwardRef } from "react";

import { FieldLabel } from "@theme/components/Form";
import { Box, Input, Label, Text, getThemeValue } from "@theme/components/ThemeUi";

import { markTextAsRequired } from "@theme/utils";

import type { MsComponentPropsWithRef, RequireField } from "@theme/types/global";

type TextFieldSharedProps = RequireField<
  Omit<MsComponentPropsWithRef<"input">, "children" | "aria-label" | "aria-labelledby">,
  "name"
> & {
  /**
   * Set label for the TextField
   */
  label?: string;
  /**
   * Updates the position of the label. Defaults to "inside"
   */
  labelPosition?: "above" | "inside";
  /**
   * Toggles the error state for the TextField
   */
  error?: string | boolean;
  /**
   * Adds a helper text below the TextField
   */
  helper?: string;
  /**
   * If an Icon is directly passed in, it will be rendered
   * or Pass in a function to render a IconButton with onclick handlers
   * See https://storybook.manscaped.io/?path=/docs/components-forms--text-area-field-with-icon-button
   */
  LeadingIcon?: FC<SVGProps<SVGElement>> | (() => JSX.Element);
  /**
   * If an Icon is directly passed in, it will be rendered
   * or Pass in a function to render a IconButton with onclick handlers
   * https://storybook.manscaped.io/?path=/docs/components-forms--text-area-field-with-icon-button
   */
  TrailingIcon?: FC<SVGProps<SVGElement>> | (() => JSX.Element);
};

type ShowLabelTrue = {
  showLabel?: true;
  label: string;
  "aria-label"?: string;
  "aria-labelledby"?: string;
};

type ShowLabelFalse_AriaLabel = {
  showLabel: false;
  label?: never;
  "aria-label": string;
  "aria-labelledby"?: string;
};

type ShowLabelFalse_AriaLabelledby = {
  showLabel: false;
  label?: never;
  "aria-label"?: string;
  "aria-labelledby": string;
};

export type TextFieldProps = TextFieldSharedProps &
  (ShowLabelTrue | ShowLabelFalse_AriaLabel | ShowLabelFalse_AriaLabelledby);

export const TextField = forwardRef<HTMLInputElement, TextFieldProps>(function TextField(
  {
    sx,
    id,
    name,
    label,
    labelPosition = "inside",
    showLabel = true,
    required = false,
    disabled = false,
    error,
    helper,
    LeadingIcon,
    TrailingIcon,
    className,
    placeholder,
    "data-error": dataError,
    "aria-labelledby": ariaLabelledByProp,
    "aria-describedby": ariaDescribedby,
    ...rest
  },
  ref,
) {
  const autoId = useId(id);
  const enableLabel = showLabel && label;

  const textLabel = enableLabel && markTextAsRequired(label, required);
  const textLabelPosition = enableLabel && label.trim().length > 35 ? "above" : labelPosition;

  const Component = enableLabel ? Label : Box;

  const hasStringError = typeof error === "string" && error.length > 0;
  const hasError = !!error;
  const labelId = textLabel && textLabelPosition === "above" ? `${autoId}_label` : undefined;

  const errorId = hasStringError ? `${autoId}_error_text` : undefined;
  const helperId = helper && !hasStringError ? `${autoId}_helper_text` : undefined;
  const ariaLabelledBy =
    textLabelPosition === "above" ? ariaLabelledByProp || labelId : ariaLabelledByProp;

  const ariaDescribedBy =
    [ariaDescribedby, errorId ?? helperId].filter(Boolean).join(" ") || undefined;

  return (
    <Box sx={{ position: "relative", ...sx }} className={className}>
      {textLabel && textLabelPosition === "above" && (
        <FieldLabel label={textLabel} error={error} data-testid="text-field-label" id={labelId} />
      )}
      <Component
        htmlFor={label ? autoId : undefined}
        sx={{
          zIndex: 1,
          position: "relative",
          height: 50,
          variant: "forms.fieldBorder",
          ...(disabled && {
            "&:hover:after": {
              borderColor: "borderLevel2",
            },
          }),
          ...(error && {
            "&:after, &:hover:after": {
              borderWidth: "default",
              borderColor: "borderError",
            },
          }),
        }}
      >
        {LeadingIcon && (
          <LeadingIcon
            data-role="leadingIcon"
            sx={{
              variant: "forms.leadingIcon",
              color: disabled ? "interactiveNeutralStrong" : "textPrimary",
            }}
          />
        )}
        <Input
          ref={ref}
          id={autoId}
          name={name}
          disabled={disabled}
          aria-labelledby={ariaLabelledBy}
          aria-describedby={error ? `${autoId}_error_text` : ariaDescribedBy}
          required={required}
          placeholder={placeholder ?? ""}
          aria-invalid={hasError || undefined}
          sx={{
            variant: "forms.field",
            cursor: disabled ? "not-allowed" : "text",
            ...(textLabel && textLabelPosition === "inside" && { pt: "s500", pb: "s200" }),
            pl: (t) =>
              LeadingIcon
                ? `calc(${getThemeValue(t, "space.s800")} + ${getThemeValue(t, "space.s300")})`
                : "s400",
            pr: TrailingIcon ? "s800" : "s400",
            bg: disabled ? "interactiveNeutralDim" : "transparent",
            ...(hasError && { bg: "backgroundError" }),
            // This is confusing so noting for future - When focused move the label up.
            // Having placeholder ?? "" gives this always a placeholder-shown state until you type in the input.
            // So this triggers whenever you type, then placeholder-shown vanishes and this condition hits.
            "&:focus, &:not(:placeholder-shown)": {
              outline: "none",
              '+ div[data-role="label"]': {
                top: 0,
                transform: (t) => `translateY(${getThemeValue(t, "space.s200")})`,
              },
            },
            // Hide the default clear icon in search inputs for webkit browsers
            "&::-webkit-search-cancel-button": { appearance: "none" },
          }}
          {...rest}
        />
        {textLabel && textLabelPosition === "inside" && (
          <FieldLabel
            label={textLabel}
            error={error}
            data-testid="text-field-label"
            sx={{
              height: "min-content",
              left: "s400",
              position: "absolute",
              mb: 0,
              // This checks the actual value of the placeholder. If it exists, move the label up. Otherwise, center.
              ...(placeholder
                ? { transform: (t) => `translateY(${getThemeValue(t, "space.s200")})`, top: 0 }
                : { transform: "translateY(-50%)", top: "50%" }),
              ...(LeadingIcon && {
                left: (t) =>
                  `calc(${getThemeValue(t, "space.s800")} + ${getThemeValue(t, "space.s300")})`,
              }),
            }}
          />
        )}
        {TrailingIcon && (
          <TrailingIcon
            data-role="trailingIcon"
            sx={{
              variant: "forms.trailingIcon",
              color: disabled ? "interactiveNeutralStrong" : "textPrimary",
            }}
          />
        )}
      </Component>
      {hasStringError && (
        <Box aria-live="polite">
          <Text
            id={`${autoId}_error_text`}
            as="small"
            variant="errorText"
            role="alert"
            data-testid="text-field-error"
            data-error={dataError}
          >
            {error}
          </Text>
        </Box>
      )}
      {!hasStringError && helper && (
        <Text as="small" id={`${autoId}_helper_text`} variant="helperText" aria-live="polite">
          {helper}
        </Text>
      )}
    </Box>
  );
});
